'use strict';

/**
 * Text-to-Speech Narrator Component
 * Uses Web Speech API to read page content aloud with text highlighting.
 * Each text node found by TreeWalker is narrated as a separate utterance,
 * followed by a pause. Highlighting applies to the entire parent element
 * of the currently spoken text node.
 */

/**
 * Gets the best available voice based on language and priority
 * Priority: default+language → localService+language → any language match → default voice
 * @param {string} language - Language code (e.g., 'da-DK', 'sv-SE')
 * @returns {SpeechSynthesisVoice|null} Selected voice or null
 */
function getBestVoice(language) {
    var voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) {
        return null;
    }

    var langCode = language.split('-')[0]; // e.g., 'da' from 'da-DK'

    // Priority 1: default voice with matching language
    for (var i = 0; i < voices.length; i++) {
        if (voices[i].default && voices[i].lang.indexOf(langCode) === 0) {
            return voices[i];
        }
    }

    // Priority 2: localService voice with matching language
    for (var j = 0; j < voices.length; j++) {
        if (voices[j].localService && voices[j].lang.indexOf(langCode) === 0) {
            return voices[j];
        }
    }

    // Priority 3: any voice with matching language
    for (var k = 0; k < voices.length; k++) {
        if (voices[k].lang.indexOf(langCode) === 0) {
            return voices[k];
        }
    }

    // Priority 4: any default voice
    for (var l = 0; l < voices.length; l++) {
        if (voices[l].default) {
            return voices[l];
        }
    }

    // Fallback: first available voice
    return voices[0];
}

/**
 * Collects visible text nodes from an element, skipping script/style/hidden nodes
 * @param {HTMLElement} element - Root element to walk
 * @returns {Array<Text>} Array of accepted text nodes
 */
function collectTextNodes(element) {
    var walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: function (node) {
                var parent = node.parentNode;
                var tagName = parent.tagName.toLowerCase();
                // Skip script, style, and hidden elements
                if (tagName === 'script' || tagName === 'style' || tagName === 'noscript') {
                    return NodeFilter.FILTER_REJECT;
                }
                // Skip if parent is hidden
                if (parent.offsetParent === null && tagName !== 'body') {
                    return NodeFilter.FILTER_REJECT;
                }
                // Skip empty text nodes
                if (!node.textContent.trim()) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        }
    );

    var nodes = [];
    var node;
    while ((node = walker.nextNode())) {
        nodes.push(node);
    }
    return nodes;
}

/**
 * Initialize narrator functionality
 */
module.exports = function () {
    var $narratorControl = $('.narrator-control');
    if (!$narratorControl.length) {
        return;
    }

    // Check browser support
    if (!window.speechSynthesis) {
        $narratorControl.hide();
        return;
    }

    // Append a skip link targeting the narrator start button into the header skip link list

    var $startBtn = $narratorControl.find('.narrator-start');
    var $pauseBtn = $narratorControl.find('.narrator-pause');
    var $stopBtn = $narratorControl.find('.narrator-stop');
    var $controls = $narratorControl.find('.narrator-controls');

    /** @type {Array<Text>} */
    var textNodes = [];
    /** @type {number} Index of the node currently being spoken */
    var currentIndex = 0;
    /** @type {SpeechSynthesisUtterance|null} */
    var currentUtterance = null;
    /** @type {boolean} */
    var isPaused = false;
    /** @type {boolean} Prevents onend from advancing after a manual cancel */
    var isStopping = false;

    var contentElement = document.querySelector('#maincontent') || document.body;

    // Get configuration from data attributes
    var pitch = parseFloat($narratorControl.data('pitch')) || 1.0;
    var rate = parseFloat($narratorControl.data('rate')) || 1.0;
    var language = document.documentElement.lang || 'da-DK';

    /**
     * Updates the pause button label and icon to reflect the current paused state
     * @param {boolean} paused - Whether narration is currently paused
     */
    function updatePauseButton(paused) {
        var pauseLabel = $pauseBtn.data('pause-label');
        var resumeLabel = $pauseBtn.data('resume-label');
        var label = paused ? resumeLabel : pauseLabel;
        var iconClass = paused ? 'fa fa-play' : 'fa fa-pause';

        $pauseBtn.attr('aria-label', label).attr('title', label);
        $pauseBtn.find('.narrator-text').text(label);
        $pauseBtn.find('.narrator-icon i').attr('class', iconClass);
    }

    /**
     * Highlights the parent element of the given text node
     * @param {Text} node - The text node being spoken
     */
    function highlightNode(node) {
        var previousHighlights = document.querySelectorAll('.narrator-highlight');
        for (var i = 0; i < previousHighlights.length; i++) {
            previousHighlights[i].classList.remove('narrator-highlight');
        }
        var parent = node.parentNode;
        if (parent && parent.classList) {
            parent.classList.add('narrator-highlight');
        }
    }

    /**
     * Clears all highlights
     */
    function clearHighlights() {
        var highlights = document.querySelectorAll('.narrator-highlight');
        for (var i = 0; i < highlights.length; i++) {
            highlights[i].classList.remove('narrator-highlight');
        }
    }

    /**
     * Speaks the text node at the given index.
     * When finished, automatically advances to the next node.
     * @param {number} index - Index into textNodes array
     */
    function speakNode(index) {
        if (index >= textNodes.length) {
            stopNarration();
            return;
        }

        currentIndex = index;
        var node = textNodes[index];

        // Highlight entire parent element for this node
        highlightNode(node);

        // Append ', , ' to create an audible pause after each node
        var text = ', , ' + node.textContent.trim() + ', ';

        currentUtterance = new SpeechSynthesisUtterance(text);
        currentUtterance.pitch = pitch;
        currentUtterance.rate = rate;
        currentUtterance.lang = language;

        var voice = getBestVoice(language);
        if (voice) {
            currentUtterance.voice = voice;
        }

        currentUtterance.onend = function () {
            if (isStopping) {
                return;
            }
            speakNode(index + 1);
        };

        currentUtterance.onerror = function (event) {
            if (isStopping) {
                return;
            }
            console.error('Speech synthesis error:', event);
            stopNarration();
        };

        window.speechSynthesis.speak(currentUtterance);
    }

    /**
     * Start or resume narration
     */
    function startNarration() {
        if (isPaused) {
            // Resume from the node where we paused
            isPaused = false;
            isStopping = false;
            updateControls(true);
            updatePauseButton(false);
            $pauseBtn.focus();
            speakNode(currentIndex);
            return;
        }

        // Fresh start — collect text nodes and begin from the first
        window.speechSynthesis.cancel();
        textNodes = collectTextNodes(contentElement);
        if (!textNodes.length) {
            return;
        }

        currentIndex = 0;
        isStopping = false;
        updateControls(true);
        updatePauseButton(false);
        $pauseBtn.focus();
        speakNode(0);
    }

    /**
     * Pause narration — cancels the current utterance and saves position
     */
    function pauseNarration() {
        if (currentUtterance && window.speechSynthesis.speaking) {
            isStopping = true;
            window.speechSynthesis.cancel();
            isPaused = true;
            clearHighlights();
            updateControls(true);
            updatePauseButton(true);
            $pauseBtn.focus();
        }
    }

    /**
     * Stop narration and reset to the beginning
     */
    function stopNarration() {
        isStopping = true;
        window.speechSynthesis.cancel();
        currentUtterance = null;
        currentIndex = 0;
        isPaused = false;
        clearHighlights();
        updateControls(false);
        updatePauseButton(false);
        $startBtn.focus();
    }

    /**
     * Update control buttons visibility
     * @param {boolean} isPlaying - Whether narration is active
     */
    function updateControls(isPlaying) {
        if (isPlaying) {
            $startBtn.hide();
            $controls.show();
        } else {
            $startBtn.show();
            $controls.hide();
        }
    }

    // Event listeners
    $startBtn.on('click', function (e) {
        e.preventDefault();
        startNarration();
    });

    $pauseBtn.on('click', function (e) {
        e.preventDefault();
        if (isPaused) {
            startNarration(); // Resume
        } else {
            pauseNarration();
        }
    });

    $stopBtn.on('click', function (e) {
        e.preventDefault();
        stopNarration();
    });

    // Keyboard support
    $narratorControl.on('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
            stopNarration();
        }
    });

    // Load voices (they may not be available immediately)
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = function () {
            // Voices are now loaded
        };
    }

    // Initial state
    updateControls(false);
};
